// Trial chambers (M22.1). PURE (ENG-1): the trial spawner's per-player wave scaling
// and state cycle, and the vault's once-per-player key unlock. A trial spawner
// watching N nearby players releases more mobs (and more at once); its ominous
// variant is harder still. When the wave is cleared it goes on cooldown, then a
// vault can be opened with a trial key — once per player.
import { TRIAL } from '../config';

/** Total mobs a trial spawner releases for `players` participants. */
export function trialWaveTotal(players: number, ominous = false): number {
  const p = Math.max(1, Math.floor(players));
  const base = ominous ? TRIAL.OMINOUS_BASE_TOTAL : TRIAL.BASE_TOTAL;
  const per = ominous ? TRIAL.OMINOUS_PER_PLAYER : TRIAL.PER_PLAYER;
  return base + (p - 1) * per;
}

/** How many of that wave may be alive at once. */
export function trialSimultaneousCap(players: number): number {
  const p = Math.max(1, Math.floor(players));
  return TRIAL.SIM_BASE + (p - 1) * TRIAL.SIM_PER_PLAYER;
}

/** Whether the spawner may release another mob right now (under the cap, wave left). */
export function trialCanSpawn(spawnedThisWave: number, aliveNow: number, players: number, ominous = false): boolean {
  return spawnedThisWave < trialWaveTotal(players, ominous) && aliveNow < trialSimultaneousCap(players);
}

export type TrialState = 'waiting' | 'active' | 'cooldown' | 'ejecting';

/**
 * Advance the spawner's state. It wakes when a player is near, releases the wave,
 * and — once every mob is spawned and slain — ejects a reward and goes on cooldown
 * before it can run again.
 */
export function nextTrialState(
  state: TrialState,
  playerNear: boolean,
  spawnedThisWave: number,
  aliveNow: number,
  players: number,
  cooldownLeft: number,
  ominous = false,
): TrialState {
  switch (state) {
    case 'waiting':
      return playerNear ? 'active' : 'waiting';
    case 'active':
      // Wave finished = everything spawned and nothing left alive.
      return spawnedThisWave >= trialWaveTotal(players, ominous) && aliveNow === 0 ? 'ejecting' : 'active';
    case 'ejecting':
      return 'cooldown';
    case 'cooldown':
      return cooldownLeft > 0 ? 'cooldown' : 'waiting';
  }
}

/** A vault opens for a player who holds the right key and hasn't opened it before. */
export function vaultUnlocks(hasKey: boolean, unlockedBy: readonly number[], playerId: number): boolean {
  return hasKey && !unlockedBy.includes(playerId);
}

/** The key an (ominous) vault requires. */
export function vaultKeyFor(ominous: boolean): string {
  return ominous ? 'ominous_trial_key' : 'trial_key';
}
